import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";

const SETTINGS_DOC_ID = "qualifierPageSettings";

export const loadQualifierSettings = async () => {
  try {
    const db = getFirestore();

    // Check if user is authenticated using localStorage token (same as AuthContext)
    const adminToken = localStorage.getItem("adminToken");

    console.log("🔍 loadQualifierSettings - Auth check:", {
      adminToken: adminToken,
      hasAdminToken: !!adminToken,
      localStorageKeys: Object.keys(localStorage),
      currentUrl:
        typeof window !== "undefined" ? window.location.href : "unknown",
    });

    if (!adminToken) {
      console.log(
        "🔍 No admin token found in loadQualifierSettings, checking page context..."
      );

      // Check if we're on an admin page as fallback
      if (typeof window !== "undefined" && window.location) {
        const currentUrl = window.location.href;
        const isOnAdminPage =
          currentUrl.includes("/admin") || currentUrl.includes("/qualifiers");

        console.log("🔍 loadQualifierSettings - Page context check:", {
          currentUrl,
          isOnAdminPage,
          willAllowLoad: isOnAdminPage,
        });

        if (isOnAdminPage) {
          console.log(
            "🔍 Allowing load based on page context (user on admin page)"
          );
          // Continue with load operation
        } else {
          console.log(
            "❌ User not on admin page and no admin token - using default settings"
          );
          return getDefaultSettings();
        }
      } else {
        console.log(
          "❌ Cannot determine page context - using default settings"
        );
        return getDefaultSettings();
      }
    } else {
      console.log(
        "✅ Admin token found in loadQualifierSettings - proceeding with load"
      );
    }

    const settingsDoc = await getDoc(
      doc(db, "qualifierSettings", SETTINGS_DOC_ID)
    );

    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      console.log("Loaded saved qualifier settings:", data);
      return data;
    } else {
      console.log("No saved qualifier settings found, using defaults");
      return getDefaultSettings();
    }
  } catch (error) {
    console.error("Error loading qualifier settings:", error);
    console.error("Error details:", {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack,
    });

    // If it's a permission error, just return defaults without throwing
    if (error.code === "permission-denied") {
      console.log("Permission denied, using default settings");
      return getDefaultSettings();
    }

    // Return default settings on error
    return getDefaultSettings();
  }
};

export const saveQualifierSettings = async (settings) => {
  try {
    const db = getFirestore();

    // Check if user is authenticated using localStorage token (same as AuthContext)
    const adminToken = localStorage.getItem("adminToken");

    console.log("🔍 localStorage check:", {
      adminToken: adminToken,
      hasAdminToken: !!adminToken,
      localStorageKeys: Object.keys(localStorage),
      allLocalStorage: Object.fromEntries(
        Object.entries(localStorage).map(([key, value]) => [
          key,
          value.substring(0, 20) + "...",
        ])
      ),
    });

    // If no admin token in localStorage, check if we're on an admin page as fallback
    if (!adminToken) {
      console.log("🔍 No admin token found, checking page context...");

      // Check if we're on an admin page (this indicates the user has admin access)
      if (typeof window !== "undefined" && window.location) {
        const currentUrl = window.location.href;
        const isOnAdminPage =
          currentUrl.includes("/admin") || currentUrl.includes("/qualifiers");

        console.log("🔍 Page context check:", {
          currentUrl,
          isOnAdminPage,
          willAllowSave: isOnAdminPage,
        });

        if (isOnAdminPage) {
          console.log(
            "🔍 Allowing save based on page context (user on admin page)"
          );
          // Continue with save operation
        } else {
          console.log(
            "❌ User not on admin page and no admin token - denying save"
          );
          throw new Error("User must be authenticated to save settings");
        }
      } else {
        console.log("❌ Cannot determine page context - denying save");
        throw new Error("User must be authenticated to save settings");
      }
    } else {
      console.log("✅ Admin token found - proceeding with save");
    }

    console.log("🔍 Save attempt - Auth state:", {
      hasAdminToken: !!adminToken,
      token: adminToken.substring(0, 20) + "...",
    });

    console.log("🔍 Settings to save:", settings);
    console.log("🔍 Document path: qualifierSettings/", SETTINGS_DOC_ID);

    const docData = {
      ...settings,
      lastUpdated: new Date().toISOString(),
      userId: "admin", // Track which user saved the settings
    };

    console.log("🔍 Final document data:", docData);

    await setDoc(doc(db, "qualifierSettings", SETTINGS_DOC_ID), docData);
    console.log("✅ Qualifier settings saved successfully");
  } catch (error) {
    console.error("Error saving qualifier settings:", error);
    console.error("Error details:", {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack,
    });

    // If it's a permission error, just log it but don't throw
    if (error.code === "permission-denied") {
      console.log(
        "Permission denied when saving settings, but continuing with local changes"
      );
      return; // Don't throw error, just return
    }

    throw new Error("Failed to save settings");
  }
};

// Helper function to get default settings
const getDefaultSettings = () => {
  return {
    columns: {
      rank: false,
      firstName: true,
      lastName: true,
      club: true,
      coach: true,
      competitions: true,
      qualifiedBadge: true,
      totalPoints: false,
      top3Scores: false,
      bestScore: false,
      levelAdvancement: false,
    },
    showRank: false,
    sortAlphabetically: true,
    showRunnersUp: true,
    runnersUpCount: 5,
  };
};
